// ─── src/api/registrations.ts ─────────────────────────────────────────────────

import axios from "axios";
import type {
  RegistrationsParams,
  RegistrationsResponse,
  PaginationMeta,
} from "../types/registration";

// ── Axios instance ─────────────────────────────────────────────────────────────
export const apiClient = axios.create({
  baseURL: "https://api.startupsyria.org/api/v1",
  timeout: 10_000,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": process.env.REACT_APP_DASHBOARD_API_KEY ?? "",
  },
});

apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const msg =
      error.response?.data?.message ??
      error.message ??
      "An unexpected error occurred";
    return Promise.reject(new Error(msg));
  }
);

// ── Normalise flat API response → internal shape with derived totalPages ───────
export interface NormalisedResponse {
  data: RegistrationsResponse["data"];
  meta: PaginationMeta;
}

const normalise = (raw: RegistrationsResponse): NormalisedResponse => ({
  data: raw.data,
  meta: {
    total:      raw.total,
    page:       raw.page,
    limit:      raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),   // ← derived here, once
  },
});

// ── API calls ──────────────────────────────────────────────────────────────────
export const getRegistrations = async (
  params: RegistrationsParams
): Promise<NormalisedResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)
  );

  const { data } = await apiClient.get<RegistrationsResponse>("/registrations", {
    params: cleanParams,
  });

  return normalise(data);
};

export const getRegistrationById = async (id: string) => {
  const { data } = await apiClient.get(`/registrations/${id}`);
  return data;
};

// ── Accept / Reject ─────────────────────────────────────────────────────────
export const updateAcceptance = async (
  id: string,
  accepted: boolean,
): Promise<import("../types/registration").Registration> => {
  const { data } = await apiClient.patch(
    `/registrations/${id}/accept`,
    { accepted },
  );
  return data;
};

// ── Edit ─────────────────────────────────────────────────────────────────────
export const updateRegistration = async (
  id: string,
  dto: Partial<import("../types/registration").Registration>,
): Promise<import("../types/registration").Registration> => {
  const { data } = await apiClient.patch(`/registrations/${id}`, dto);
  return data;
};

// ── Delete ───────────────────────────────────────────────────────────────────
export const deleteRegistration = async (id: string): Promise<void> => {
  await apiClient.delete(`/registrations/${id}`);
};